// src/utils/nlpUtils.js — Pure JS NLP algorithms, zero external dependencies

// ─── SAMPLE TEXTS ─────────────────────────────────────────────────────────────
export const SAMPLE_TEXTS = {
  tech:    `Artificial intelligence is transforming industries at an unprecedented pace. Machine learning models now power everything from search engines to medical diagnosis. Researchers at major universities and technology companies are pushing the boundaries of what computers can understand and generate. Natural language processing has seen remarkable advances, with large language models demonstrating human-level performance on many benchmark tasks. However, concerns about bias, safety, and the societal impact of these systems continue to grow among ethicists and policymakers worldwide. Deep learning architectures have proven surprisingly effective across domains including vision, speech, and language understanding.`,
  review:  `This product exceeded my expectations in every way. The build quality is exceptional and the performance is outstanding. I was initially very skeptical about the high price, but after using it for three months I can confidently say it was absolutely worth every penny. The customer service team was incredibly helpful and responsive when I had a minor setup issue. I would highly recommend this to anyone looking for a reliable and high-quality solution. The only minor complaint is that the packaging could be more environmentally friendly. Overall an amazing purchase that I do not regret at all.`,
  science: `Quantum computing represents a fundamental shift in computational paradigms. Unlike classical bits that exist in binary states, quantum bits or qubits can exist in superposition, simultaneously representing both zero and one. This property combined with quantum entanglement allows quantum computers to solve certain problems exponentially faster than classical machines. Current processors suffer from decoherence and high error rates limiting practical applications. Researchers are actively developing error correction techniques and more stable qubit implementations using superconducting circuits, trapped ions, and photonic systems. IBM, Google, and IonQ are leading the commercial race.`,
  story:   `The old lighthouse stood alone on the rocky cliff, its beam sweeping across the dark waters below. Captain Marina had kept the light burning for thirty years, through storms that shook the earth and fog so thick you could barely see your hand. Tonight felt different. The sea was unnaturally calm, the stars unusually bright. A small boat appeared on the horizon, moving steadily toward the rocks. Marina reached for the foghorn, her hands trembling with both age and urgency. She had saved eleven ships in her long career. She was not ready to stop now. The light must keep turning.`,
  mixed:   `The conference was absolutely fantastic! Dr. Sarah Johnson from MIT presented groundbreaking research on neural networks. Unfortunately the hotel WiFi was terrible and the coffee was completely undrinkable. Amazon CEO Andy Jassy announced a new $2.5 billion investment in cloud infrastructure on Tuesday March 15. The stock rose 3.5% to $185.40 following the announcement in New York. Professor Chen warned that despite the excitement, we must remain cautious about overhyped AI applications. San Francisco and London both reported record attendance at the AI Summit 2024 event.`,
};

// ─── STOP WORDS ───────────────────────────────────────────────────────────────
export const STOP_WORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with','by',
  'from','as','is','was','are','were','be','been','being','have','has','had',
  'do','does','did','will','would','could','should','may','might','can','shall',
  'i','me','my','we','our','you','your','he','him','his','she','her','it','its',
  'they','them','their','this','that','these','those','what','which','who',
  'am','not','no','nor','so','yet','both','either','some','such','than','too',
  'very','just','about','after','before','into','through','during','each','few',
  'more','also','up','out','then','there','when','where','while','how','all',
  'any','only','own','same','other','now','here','even','back','still','well',
]);

// ─── TOKENIZATION ─────────────────────────────────────────────────────────────
export function tokenizeWords(text) {
  return (text.toLowerCase().match(/\b[a-z][a-z']*\b/g) || []);
}
export function tokenizeSentences(text) {
  const raw = text.match(/[^.!?…]+[.!?…]+(?:\s|$)|[^.!?…]+$/g) || [text];
  return raw.map(s => s.trim()).filter(s => s.length > 4);
}
export function removeStopWords(tokens) {
  return tokens.filter(t => !STOP_WORDS.has(t) && t.length > 1);
}
export function tokenizeNgrams(tokens, n) {
  const out = [];
  for (let i = 0; i <= tokens.length - n; i++) out.push(tokens.slice(i, i+n).join(' '));
  return out;
}

// ─── STEMMING (Porter lite) ───────────────────────────────────────────────────
export function stem(word) {
  let w = word.toLowerCase().replace(/[^a-z]/g,'');
  if (w.length < 4) return w;
  if      (w.endsWith('sses')) w = w.slice(0,-2);
  else if (w.endsWith('ies'))  w = w.slice(0,-2);
  else if (w.endsWith('ss'))   {}
  else if (w.endsWith('s') && w.length > 4) w = w.slice(0,-1);
  if      (w.endsWith('eed') && w.length > 5) w = w.slice(0,-1);
  else if (w.endsWith('ing') && w.length > 6) w = w.slice(0,-3);
  else if (w.endsWith('ed')  && w.length > 5) w = w.slice(0,-2);
  [['ational','ate'],['tional','tion'],['ization','ize'],['fulness','ful'],
   ['ousness','ous'],['iveness','ive'],['ness',''],['ment',''],['ful',''],
   ['ous',''],['ive',''],['ely',''],['ly','']].forEach(([s,r]) => {
    if (w.endsWith(s) && w.length > s.length+2) w = w.slice(0,-s.length)+r;
  });
  return w || word.slice(0,3);
}

// ─── TEXT STATISTICS ──────────────────────────────────────────────────────────
function countSyllables(w) {
  w = w.toLowerCase().replace(/[^a-z]/g,'');
  if (w.length <= 3) return 1;
  const m = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/,'').match(/[aeiouy]{1,2}/g);
  return Math.max(1, m ? m.length : 1);
}
export function textStats(text) {
  const words     = tokenizeWords(text);
  const sentences = tokenizeSentences(text);
  const unique    = new Set(words);
  const noStop    = removeStopWords(words).filter(w=>w.length>2);
  const freq={};
  words.forEach(w=>{freq[w]=(freq[w]||0)+1;});
  const filtFreq={};
  noStop.forEach(w=>{filtFreq[w]=(filtFreq[w]||0)+1;});
  const topWords = Object.entries(filtFreq).sort((a,b)=>b[1]-a[1]).slice(0,25)
    .map(([word,count])=>({word,count,pct:+(count/words.length*100).toFixed(2)}));
  const sentLengths = sentences.map(s=>tokenizeWords(s).length);
  const syllables   = words.reduce((s,w)=>s+countSyllables(w),0);
  const fkEase = 206.835 - 1.015*(words.length/(sentences.length||1)) - 84.6*(syllables/(words.length||1));
  const fkGrade= 0.39*(words.length/(sentences.length||1)) + 11.8*(syllables/(words.length||1)) - 15.59;
  return {
    wordCount:   words.length,
    uniqueWords: unique.size,
    sentences:   sentences.length,
    chars:       text.length,
    avgWordLen:  +(words.reduce((s,w)=>s+w.length,0)/(words.length||1)).toFixed(2),
    avgSentLen:  +(sentLengths.reduce((a,b)=>a+b,0)/(sentLengths.length||1)).toFixed(1),
    lexDiv:      +(unique.size/(words.length||1)).toFixed(3),
    readability: +Math.max(0,Math.min(100,fkEase)).toFixed(1),
    gradeLevel:  +Math.max(0,fkGrade).toFixed(1),
    topWords, sentLengths, syllables,
  };
}

// ─── TF-IDF ───────────────────────────────────────────────────────────────────
export function computeTFIDF(docs) {
  const tokenized = docs.map(d=>removeStopWords(tokenizeWords(d.text)));
  const N = docs.length;
  const tfMaps = tokenized.map(tokens=>{
    const m={};tokens.forEach(t=>{m[t]=(m[t]||0)+1;});
    const total=tokens.length||1;
    Object.keys(m).forEach(t=>{m[t]/=total;});
    return m;
  });
  const df={};
  tokenized.forEach(tokens=>new Set(tokens).forEach(t=>{df[t]=(df[t]||0)+1;}));
  const idf={};
  Object.keys(df).forEach(t=>{idf[t]=Math.log((N+1)/(df[t]+1))+1;});
  const allTerms=Object.keys(idf).sort();
  const topPerDoc=tokenized.map((_,di)=>
    allTerms.map(t=>({term:t,tfidf:+((tfMaps[di][t]||0)*(idf[t]||0)).toFixed(4),tf:+(tfMaps[di][t]||0).toFixed(4),idf:+(idf[t]||0).toFixed(3)}))
      .filter(x=>x.tfidf>0).sort((a,b)=>b.tfidf-a.tfidf).slice(0,12)
  );
  const globalTop=allTerms.map(t=>{
    const avg=tfMaps.reduce((s,m)=>s+(m[t]||0),0)/N*(idf[t]||0);
    return {term:t,idf:+(idf[t]||0).toFixed(3),avgTfidf:+avg.toFixed(4)};
  }).sort((a,b)=>b.avgTfidf-a.avgTfidf).slice(0,30);
  return {topPerDoc,globalTop,idf,tfMaps,allTerms,N};
}
export function cosineSimilarity(a,b){
  const dot=a.reduce((s,v,i)=>s+v*(b[i]||0),0);
  const magA=Math.sqrt(a.reduce((s,v)=>s+v*v,0));
  const magB=Math.sqrt(b.reduce((s,v)=>s+v*v,0));
  return (magA&&magB)?+(dot/(magA*magB)).toFixed(4):0;
}
export function tfidfVector(tokens,idf,terms){
  const m={};tokens.forEach(t=>{m[t]=(m[t]||0)+1;});
  const total=tokens.length||1;
  return terms.map(t=>+((m[t]||0)/total*(idf[t]||0)).toFixed(4));
}

// ─── SENTIMENT ────────────────────────────────────────────────────────────────
export const SENTIMENT_LEXICON = {
  good:2,great:3,excellent:4,amazing:4,wonderful:3,fantastic:4,outstanding:4,
  superb:4,perfect:4,love:3,best:3,awesome:4,incredible:4,magnificent:3,
  positive:2,happy:3,glad:2,pleased:2,satisfied:2,delighted:3,enjoy:2,
  beautiful:2,nice:2,better:2,helpful:2,useful:2,effective:2,efficient:2,
  reliable:2,recommend:3,worth:2,impressive:3,exceptional:4,remarkable:3,
  phenomenal:4,exciting:3,confident:2,successful:3,innovative:2,inspiring:3,
  powerful:2,quality:2,superior:3,brilliant:3,friendly:2,responsive:2,
  bad:-2,terrible:-4,horrible:-4,awful:-3,dreadful:-3,disappointing:-3,
  poor:-2,worst:-4,hate:-3,ugly:-2,nasty:-3,disgusting:-4,offensive:-3,
  annoying:-2,frustrated:-2,frustrating:-3,angry:-3,upset:-2,unhappy:-2,
  sad:-2,depressed:-3,miserable:-3,failure:-3,problem:-2,issue:-2,
  useless:-3,waste:-2,expensive:-1,disaster:-4,ruined:-3,broken:-3,
  difficult:-1,wrong:-2,slow:-1,buggy:-2,unreliable:-2,disappoints:-3,
};
export const INTENSIFIERS = new Set(['very','extremely','incredibly','absolutely','completely','totally','utterly','highly','especially','remarkably','exceptionally','quite','rather','so','super']);
export const NEGATIONS    = new Set(['not','never','no','neither','hardly','barely','scarcely','nothing','nobody','nowhere','without','cant','cannot','dont','doesnt','didnt','wasnt','wont','wouldnt','shouldnt']);

export function analyzeSentiment(text){
  const sentences=tokenizeSentences(text);
  const scoreSent=sent=>{
    const tokens=tokenizeWords(sent);
    let score=0;
    for(let i=0;i<tokens.length;i++){
      const w=tokens[i];
      if(!(w in SENTIMENT_LEXICON))continue;
      const negated=tokens.slice(Math.max(0,i-3),i).some(t=>NEGATIONS.has(t));
      const intensified=tokens.slice(Math.max(0,i-2),i).some(t=>INTENSIFIERS.has(t));
      score+=SENTIMENT_LEXICON[w]*(negated?-1:1)*(intensified?1.5:1);
    }
    return +(score/Math.sqrt(tokens.length||1)).toFixed(3);
  };
  const results=sentences.map(sent=>{
    const score=scoreSent(sent);
    const label=score>0.15?'positive':score<-0.15?'negative':'neutral';
    return {sentence:sent.length>90?sent.slice(0,90)+'…':sent,score,label,conf:+Math.min(1,Math.abs(score)).toFixed(2)};
  });
  const overall=results.reduce((s,r)=>s+r.score,0)/(results.length||1);
  return {
    sentences:results,overall:+overall.toFixed(3),
    label:overall>0.1?'positive':overall<-0.1?'negative':'neutral',
    pos:results.filter(r=>r.label==='positive').length,
    neg:results.filter(r=>r.label==='negative').length,
    neu:results.filter(r=>r.label==='neutral').length,
  };
}

// ─── NER ──────────────────────────────────────────────────────────────────────
const TITLES  =new Set(['mr','mrs','ms','dr','prof','professor','sir','lord','captain','president','senator','general','colonel','director','ceo','cto','cfo','chief']);
const ORG_SFX =new Set(['inc','corp','ltd','llc','co','company','corporation','organization','institute','university','college','school','hospital','bank','fund','group','foundation','committee','agency','bureau','ministry']);
const LOC_IND =new Set(['city','town','country','state','province','county','district','region','street','avenue','road','island','mountain','river','lake','ocean','sea','gulf','valley','bay']);
const MONTHS  =new Set(['january','february','march','april','may','june','july','august','september','october','november','december']);
const WEEKDAYS=new Set(['monday','tuesday','wednesday','thursday','friday','saturday','sunday']);
export function extractEntities(text){
  const words=text.split(/\s+/);
  const entities=[];
  const cl=w=>w.replace(/[^a-zA-Z0-9'$%.-]/g,'');
  const lo=w=>cl(w).toLowerCase();
  for(let i=0;i<words.length;i++){
    const w=cl(words[i]),l=w.toLowerCase();
    const nxt=cl(words[i+1]||''),prv=lo(words[i-1]||'');
    if(TITLES.has(l)&&/^[A-Z]/.test(nxt)){
      const nm=nxt+(cl(words[i+2]||'').match(/^[A-Z][a-z]+$/)?(' '+cl(words[i+2])):'');
      entities.push({text:w+' '+nm,type:'PERSON',i});i+=nm.split(' ').length;continue;
    }
    if(/^[A-Z][a-z]{1,}$/.test(w)&&/^[A-Z][a-z]{1,}$/.test(nxt)&&!STOP_WORDS.has(l)&&!TITLES.has(l)){
      entities.push({text:w+' '+nxt,type:'PERSON',i});i++;continue;
    }
    if(/^[A-Z]{2,5}$/.test(w)&&w.length>=2){entities.push({text:w,type:'ORG',i});continue;}
    if(/^[A-Z]/.test(w)&&ORG_SFX.has(lo(words[i+1]||''))){entities.push({text:w+' '+cl(words[i+1]),type:'ORG',i});i++;continue;}
    if(/^[A-Z][a-z]+$/.test(w)&&(LOC_IND.has(prv)||prv==='in'||prv==='at'||prv==='from')&&!STOP_WORDS.has(l)){entities.push({text:w,type:'LOC',i});continue;}
    if(/^\$[\d,]+(\.\d+)?$/.test(w)||/^\d+(\.\d+)?%$/.test(w)||/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(w)){entities.push({text:w,type:'NUM',i});continue;}
    if(MONTHS.has(l)||WEEKDAYS.has(l)||(/(19|20)\d{2}/.test(w))){entities.push({text:w,type:'TIME',i});continue;}
  }
  const seen=new Set();
  const unique=entities.filter(e=>{const k=e.text.toLowerCase();if(seen.has(k))return false;seen.add(k);return true;});
  const byType={PERSON:[],ORG:[],LOC:[],NUM:[],TIME:[]};
  unique.forEach(e=>{if(byType[e.type])byType[e.type].push(e);});
  return {entities:unique,byType,counts:Object.fromEntries(Object.entries(byType).map(([k,v])=>[k,v.length]))};
}

// ─── N-GRAM LANGUAGE MODEL ────────────────────────────────────────────────────
export function buildNgramLM(text,n=2){
  const tokens=tokenizeWords(text);
  const model={};
  for(let i=0;i<=tokens.length-n;i++){
    const ctx=tokens.slice(i,i+n-1).join(' ');
    const next=tokens[i+n-1];
    if(!model[ctx])model[ctx]={};
    model[ctx][next]=(model[ctx][next]||0)+1;
  }
  Object.keys(model).forEach(ctx=>{
    const total=Object.values(model[ctx]).reduce((s,v)=>s+v,0);
    Object.keys(model[ctx]).forEach(w=>{model[ctx][w]/=total;});
  });
  return {model,n,vocabSize:new Set(tokens).size,tokenCount:tokens.length};
}
export function generateText(lm,seedTokens,length=30){
  const {model,n}=lm;
  const out=[...seedTokens];
  for(let i=0;i<length;i++){
    const ctx=out.slice(-(n-1)).join(' ');
    const dist=model[ctx];
    if(!dist)break;
    const rand=Math.random();let cum=0,picked=Object.keys(dist)[0];
    for(const [w,p] of Object.entries(dist)){cum+=p;if(rand<cum){picked=w;break;}}
    out.push(picked);
  }
  return out.join(' ');
}
export function computePerplexity(text,lm){
  const {model,n}=lm;
  const tokens=tokenizeWords(text);
  let logSum=0,count=0;
  for(let i=n-1;i<tokens.length;i++){
    const ctx=tokens.slice(i-n+1,i).join(' ');
    const prob=model[ctx]?.[tokens[i]]||1e-10;
    logSum+=Math.log2(prob);count++;
  }
  return count>0?+Math.pow(2,-logSum/count).toFixed(2):Infinity;
}

// ─── EMBEDDINGS ───────────────────────────────────────────────────────────────
export function buildCooccurrence(text,windowSize=3){
  const tokens=removeStopWords(tokenizeWords(text));
  const vocab=[...new Set(tokens)].slice(0,40);
  const idx=Object.fromEntries(vocab.map((w,i)=>[w,i]));
  const mat=vocab.map(()=>new Array(vocab.length).fill(0));
  for(let i=0;i<tokens.length;i++){
    for(let j=Math.max(0,i-windowSize);j<=Math.min(tokens.length-1,i+windowSize);j++){
      if(i!==j&&idx[tokens[i]]!==undefined&&idx[tokens[j]]!==undefined)
        mat[idx[tokens[i]]][idx[tokens[j]]]++;
    }
  }
  return {vocab,mat};
}
export function project2D(text){
  const{vocab,mat}=buildCooccurrence(text,3);
  if(vocab.length<4)return[];
  const sums=mat.map(r=>r.reduce((s,v)=>s+v,0)||1);
  const norm=mat.map((r,i)=>r.map(v=>v/sums[i]));
  const n=vocab.length;
  const seed1=norm.map(r=>r.slice(0,Math.ceil(n/2)).reduce((s,v)=>s+v,0));
  const seed2=norm.map(r=>r.slice(Math.floor(n/2)).reduce((s,v)=>s+v,0));
  const normalize=v=>{const m=Math.sqrt(v.reduce((s,x)=>s+x*x,0))||1;return v.map(x=>x/m);};
  const v1=normalize(seed1),v2=normalize(seed2);
  return vocab.map((word,i)=>({
    word,
    x:+norm[i].reduce((s,v,j)=>s+v*(v1[j]||0),0).toFixed(4),
    y:+norm[i].reduce((s,v,j)=>s+v*(v2[j]||0),0).toFixed(4),
    freq:mat[i].reduce((s,v)=>s+v,0),
  }));
}

// ─── COLLOCATION & PMI ────────────────────────────────────────────────────────
export function computePMI(text){
  const tokens=removeStopWords(tokenizeWords(text));
  const N=tokens.length;
  const uni={},bi={};
  tokens.forEach(t=>{uni[t]=(uni[t]||0)+1;});
  for(let i=0;i<tokens.length-1;i++){
    const k=tokens[i]+' '+tokens[i+1];
    bi[k]=(bi[k]||0)+1;
  }
  const pmi=Object.entries(bi)
    .filter(([,c])=>c>=2)
    .map(([pair,c])=>{
      const [a,b]=pair.split(' ');
      const pAB=c/N,pA=(uni[a]||1)/N,pB=(uni[b]||1)/N;
      return{pair,count:c,pmi:+(Math.log2(pAB/(pA*pB))).toFixed(3)};
    })
    .sort((a,b)=>b.pmi-a.pmi).slice(0,20);
  return pmi;
}

// ─── EDIT DISTANCE & SIMILARITY ───────────────────────────────────────────────
export function editDistance(s,t){
  const m=s.length,n2=t.length;
  const dp=Array.from({length:m+1},(_,i)=>Array.from({length:n2+1},(_,j)=>i?j?0:i:j));
  for(let i=1;i<=m;i++)for(let j=1;j<=n2;j++)
    dp[i][j]=s[i-1]===t[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1]);
  return dp[m][n2];
}
export function jaccardSim(a,b){
  const sa=new Set(removeStopWords(tokenizeWords(a)));
  const sb=new Set(removeStopWords(tokenizeWords(b)));
  const inter=[...sa].filter(x=>sb.has(x)).length;
  return new Set([...sa,...sb]).size?+(inter/new Set([...sa,...sb]).size).toFixed(4):0;
}
